/**
 * Canonical in-play scoreboard for the `/leaguesdemo` India v WI walkthrough.
 * Lobby match centre, Sixes, Bullion, Trader, and Squares all read this so the
 * over and totals stay the same after Start the match.
 */

export const PRESENTER_LIVE_HOME = "India";
export const PRESENTER_LIVE_AWAY = "West Indies";
export const PRESENTER_LIVE_COMPETITION = "ICC Men's T20 World Cup";
export const PRESENTER_LIVE_OVER = 12;
export const PRESENTER_LIVE_MAX_OVERS = 20;
export const PRESENTER_LIVE_BALLS_THROUGH = 4;
export const PRESENTER_LIVE_BALL_RESULTS = ["6", "4", "1", "1", "0", "2"] as const;
export const PRESENTER_LIVE_BASE_RUNS = 110;
export const PRESENTER_LIVE_BASE_WKTS = 2;
export const PRESENTER_LIVE_BOWLER = "Alzarri Joseph";
export const PRESENTER_LIVE_BATTER_1 = "Hardik Pandya";
export const PRESENTER_LIVE_BATTER_2 = "Rinku Singh";

function deliveryRuns(raw: string): number {
    const v = raw.trim().toUpperCase();
    if (!v || v === "W") return 0;
    if (/^W\+\d$/.test(v)) return parseInt(v.slice(2), 10) || 0;
    if (/^\d+W$/.test(v) || v === "WD") return parseInt(v, 10) || 1;
    if (/^\d+NB$/.test(v)) return parseInt(v, 10) || 1;
    if (/^\d+L?B$/.test(v)) return parseInt(v, 10) || 0;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
}

function legalBallsIn(results: readonly string[], through: number): number {
    let n = 0;
    for (let i = 0; i < through; i += 1) {
        const v = String(results[i] ?? "").trim().toUpperCase();
        if (!v) continue;
        if (v.includes("WD") || v.includes("WIDE") || v.includes("NB")) continue;
        n += 1;
    }
    return n;
}

export type PresenterLiveMatchCard = {
    home: string;
    away: string;
    batting: string;
    competition: string;
    overNumber: number;
    maxOvers: number;
    completedOvers: number;
    legalBalls: number;
    oversStr: string;
    oversLabel: string;
    inns1Runs: number;
    inns1Wkts: number;
    scoreLineHome: string;
    scoreLineAway: string;
    scoreLines: readonly [string, string];
    statusLine: string;
    innsNow: 1;
    format: 20;
    bowler: string;
    batter1: string;
    batter2: string;
    ballResults: readonly string[];
    overDelivery: string[];
    commentaryStatus: string;
    currentInningsOversDecimal: number;
    curBall: string | null;
};

export function presenterLiveMatchCard(
    ballsThrough: number = PRESENTER_LIVE_BALLS_THROUGH,
): PresenterLiveMatchCard {
    const results = PRESENTER_LIVE_BALL_RESULTS;
    const through = Math.max(0, Math.min(results.length, Math.floor(Number(ballsThrough) || 0)));
    const overRuns = results.slice(0, through).reduce((sum, raw) => sum + deliveryRuns(raw), 0);
    const legal = legalBallsIn(results, through);
    const completedOvers = Math.max(0, PRESENTER_LIVE_OVER - 1);
    const part = Math.min(6, legal);
    const oversStr = `${completedOvers}.${part}`;
    const inns1Runs = PRESENTER_LIVE_BASE_RUNS + overRuns;
    const inns1Wkts = PRESENTER_LIVE_BASE_WKTS;
    const scoreLineHome = `${inns1Runs}/${inns1Wkts}`;
    const scoreLineAway = "0/0";
    return {
        home: PRESENTER_LIVE_HOME,
        away: PRESENTER_LIVE_AWAY,
        batting: PRESENTER_LIVE_HOME,
        competition: PRESENTER_LIVE_COMPETITION,
        overNumber: PRESENTER_LIVE_OVER,
        maxOvers: PRESENTER_LIVE_MAX_OVERS,
        completedOvers,
        legalBalls: part,
        oversStr,
        oversLabel: `${oversStr}/${PRESENTER_LIVE_MAX_OVERS}`,
        inns1Runs,
        inns1Wkts,
        scoreLineHome,
        scoreLineAway,
        scoreLines: [scoreLineHome, scoreLineAway],
        statusLine: `${scoreLineHome} (${oversStr}) v ${scoreLineAway} Match in Progress`,
        innsNow: 1,
        format: 20,
        bowler: PRESENTER_LIVE_BOWLER,
        batter1: PRESENTER_LIVE_BATTER_1,
        batter2: PRESENTER_LIVE_BATTER_2,
        ballResults: results,
        overDelivery: results.slice(0, through).map((b) => String(b)),
        commentaryStatus: `${PRESENTER_LIVE_BOWLER} to ${PRESENTER_LIVE_BATTER_1}`,
        currentInningsOversDecimal: completedOvers + part / 6,
        curBall: part < 6 ? `${completedOvers}.${part + 1}` : null,
    };
}
